import { Box, Button, Tooltip, Typography, useTheme } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import React from 'react'

import thinkingSimba from '../../../../assets/simba-ai/Thinking.png'
import { useSimbaAIStore } from '../../../../store/simbaai.store'
import { formatDate, Icons } from '../../../../utils/utilities'
import { useUserStore } from '../../../../store/user.store'

const SubjectPracticeSessions = ( { enterFullScreen } ) => {

    const { palette, layout, border, shadows } = useTheme()

    const SimbaAIStore = useSimbaAIStore()
    const UserStore = useUserStore()

    const [searchParams, setSearchParams] = useSearchParams()
    const selectedSubject = searchParams.get( 'selectedSubject' )

    let subjectDetails = UserStore.getUserSubjectsDetails.allSubjects?.find( sub => sub.subject_code === selectedSubject )
    if ( !subjectDetails ) {
        subjectDetails = SimbaAIStore.getPracticeSessions.filter( ps => ps.subject.subject_code === selectedSubject ).at( 0 )?.subject
        if ( !subjectDetails )
            subjectDetails = { subject_code: selectedSubject }
    }


    const setSearchParamsValues = ( values ) => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            for ( const key in values ) {
                if ( !Object.hasOwn( values, key ) ) continue
                if ( values[key] )
                    newParams.set( key, values[key] )
                else
                    newParams.delete( key )
            }
            return newParams
        }, { replace: true } )
    }

    const backToHome = () => {
        setSearchParamsValues( { selectedSubject: "" } )
        SimbaAIStore.setView( "home", "right" )
    }

    const startQuiz = async ( practiceSession ) => {
        setSearchParamsValues( { practiceQuiz: practiceSession._id } )
        SimbaAIStore.setView( "loading" )
        const { success, data } = await SimbaAIStore.fetchPracticeSessionDetails( practiceSession._id )

        if ( success ) {
            if ( data.incomplete_session_exists ) {
                SimbaAIStore.setView( "continue-pending", "left", false )
            } else {
                if ( data.practiced ) {
                    SimbaAIStore.setView( "start-again", "left", false )
                } else {
                    const success = await SimbaAIStore.fetchPracticeSessionQuestions( practiceSession._id )
                    if ( success ) {
                        SimbaAIStore.setView( "question", "left", false )
                        enterFullScreen()
                    }
                }
            }
        }
    }

    return (
        <Box minHeight={0} display="flex" padding="20px" flexGrow={1} flexDirection="column" justifyContent="flex-start" alignItems="flex-start" gap="20px">
            <Box flexShrink={0} width="100%" padding={layout.pagePadding} borderRadius="10px" gap="20px" alignItems="center" display="flex" sx={{
                background: palette.primary.light,
                borderBottom: `4px solid ${palette.primary.light + "99"}`,
                borderLeft: `4px solid ${palette.primary.light + "99"}`,
            }} >
                <Box component="img" height="120px" src={thinkingSimba} />
                <Box>
                    <Typography lineHeight={1} color={palette.common.white} fontSize="28px" fontWeight={800}>{subjectDetails.subject_name || subjectDetails.subject_code}</Typography>
                    <Typography fontSize="12px" color={palette.common.white} fontWeight={500}>{SimbaAIStore.getPracticeSessions.filter( ps => ps.subject.subject_code === selectedSubject ).length} practice sessions available.</Typography>

                    <Box flexWrap="wrap" flexGrow={{ xs: "1", sm: "initial" }} marginTop="10px" display="flex" gap={layout.cardsGap} alignItems="center" >
                        <Button onClick={backToHome} sx={{ color: palette.common.white, border: `1px solid ${palette.common.white}`, borderBottom: `4px solid ${palette.common.white}`, borderRadius: "10px" }} startIcon={Icons.default.ArrowBack}  >Back to home</Button>
                    </Box>
                </Box>
            </Box>

            <Box
                gap={layout.cardsGap}
                display="grid"
                gridTemplateColumns={{
                    md: "repeat(4,1fr)",
                    sm: "repeat(2,1fr)",
                    sx: "repeat(1,1fr)",
                }}
            >
                {SimbaAIStore.getPracticeSessions.filter( ps => ps.subject.subject_code === selectedSubject ).map( practiceSession => {

                    const topics = Object.values( practiceSession.modules_and_topics ).map( mod => mod.topics ).flat()

                    return (
                        <Box
                            onClick={() => startQuiz( practiceSession )}
                            flexGrow={{ xs: 1, sm: "initial" }}
                            className="animation-wave"
                            overflow="hidden"
                            key={practiceSession._id}
                            display="flex"
                            gap="20px"
                            alignItems="flex-start"
                            padding="10px 20px"
                            borderRadius="10px"
                            boxShadow={shadows[1]}
                            border={border[1]}
                            borderBottom={`4px solid ${palette.borderColor}`}
                            position="relative"
                            bgcolor={palette.contentBg}
                            sx={{
                                cursor: "pointer",
                                transition: "200ms",
                                ":hover": {
                                    borderColor: palette.primary.light,
                                    transform: `skew(-5deg)`
                                }
                            }}
                        >
                            <Box display="flex" fontSize="20px" color={palette.primary.contrastText} justifyContent="center" width="40px" height="40px" sx={{ aspectRatio: "1 / 1" }} borderRadius="50%" bgcolor={palette.primary.dark} alignItems="center" >{Icons.default.QuizIconOutlined}</Box>
                            <Box display="flex" flexGrow={1} overflow="hidden" flexDirection="column" alignItems="flex-start">
                                <Typography fontWeight={300} color='textSecondary' fontSize="10px" >{formatDate( practiceSession.date_range_start )} to {formatDate( practiceSession.date_range_end )}</Typography>
                                <Tooltip disableInteractive title={practiceSession.title}>
                                    <Typography fontSize="18px" width="100%" overflow="hidden" noWrap fontWeight={600}>{practiceSession.title}</Typography>
                                </Tooltip>
                                <Typography width="100%" display="flex" noWrap alignItems="center" gap="5px" fontSize="12px"  > {Icons.default.Sort2} {`Difficulty level: ${practiceSession.complexity}`}</Typography>
                                <Typography width="100%" display="flex" noWrap alignItems="center" gap="5px" fontSize="12px" >{Icons.default.User} {practiceSession.subject.subject_code} - {practiceSession.creator_info.name}</Typography>
                                <Box color={palette.text.secondary} display="flex" gap="5px" fontSize="14px" alignItems="center">
                                    {topics.slice( 0, 1 ).map( ( topic, index ) => (
                                        <Typography title={topic} maxWidth="100px" noWrap fontSize="12px" key={topic} borderRadius="5px" color='textPrimary' border={`1px solid ${palette.text.primary}`} bgcolor={palette.contentBg} padding="2px 5px" >{topic}</Typography>
                                    ) )}
                                    {topics.length > 1 && <Typography fontSize="12px" borderRadius="5px" color='textPrimary' border={`1px solid ${palette.text.primary}`} bgcolor={palette.contentBg} padding="2px 5px" >+{topics.length - 1} More</Typography>}
                                </Box>
                            </Box>
                        </Box>
                    )
                } )}
            </Box>

        </Box>
    )
}

export default SubjectPracticeSessions
